/**
 * COSHH entity DTOs.
 *
 * Mirrors Prisma `CoshHSubstance` (the substance catalogue) and
 * `ProjectCoshHSubstance` (the per-project link with notes). Account-level
 * custom substances (`AccountCustomCoshh`) are surfaced separately via
 * GET /api/mobile/v1/account/coshh — they don't appear in this file because
 * they're tied to the user, not a project.
 *
 * Ids are cuid() on both Prisma models.
 */
import { z } from "zod";
import { entityIdSchema } from "../common";
import { CustomCoshhSchema, CustomCoshhStrict } from "./custom-coshh";

/**
 * Catalogue substance — the canonical "Acetone" / "Cement dust" etc. row
 * shared across all users. Read-only over the API.
 */
export const CoshhSubstanceSchema = z.object({
  id: z.string().cuid(),
  slug: z.string(),
  name: z.string(),
  category: z.string(),
  description: z.string(),
  welLimit: z.string().nullable(),
  healthEffects: z.string(),
  exposureRoutes: z.string(),
  controlMeasures: z.string(),
  emergencyFirstAid: z.string(),
  disposal: z.string(),
  sdsFileUrl: z.string().nullable(),
  aliases: z.array(z.string()),
  createdAt: z.string().datetime(),
});

// #2411 server-boundary variant: the mobile app parses the permissive base;
// the routes serialise through the Strict form.
export const CoshhSubstanceStrict = CoshhSubstanceSchema.strict();

export type CoshhSubstance = z.infer<typeof CoshhSubstanceSchema>;

/**
 * Account-level custom COSHH entry (Prisma AccountCustomCoshh). Created by
 * the user when the catalogue doesn't cover a substance they handle.
 */
export const CoshhCustomAccountSchema = z
  .object({
    id: entityIdSchema,
    userId: z.string(),
    name: z.string(),
    category: z.string(),
    welLimit: z.string().nullable(),
    healthEffects: z.string().nullable(),
    controlMeasures: z.string().nullable(),
    exposureRoutes: z.string().nullable(),
    ppeRequired: z.string().nullable(),
    firstAidMeasures: z.string().nullable(),
    createdAt: z.string().datetime(),
  })
  .strict();

export type CoshhCustomAccount = z.infer<typeof CoshhCustomAccountSchema>;

/**
 * The per-project link row (Prisma ProjectCoshHSubstance). Surfaces the
 * project-specific notes; the substance card itself comes back as a nested
 * `substance` field.
 */
export const ProjectCoshhRecordSchema = z.object({
  id: z.string().cuid(),
  projectId: entityIdSchema,
  // A row links EITHER a catalogue substance (substanceId + `substance`) OR an
  // account custom / SDS substance (accountCustomCoshhId + `customSubstance`).
  // Exactly one side is populated; the other is null/absent.
  substanceId: z.string().nullable(),
  accountCustomCoshhId: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
  substance: CoshhSubstanceSchema.optional(),
  customSubstance: CustomCoshhSchema.optional(),
  // G5-2 (#992) site-specific assessment — returned so mobile can show/edit it.
  quantityUse: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  personsExposed: z.string().nullable().optional(),
  siteControls: z.string().nullable().optional(),
  residualRisk: z.string().nullable().optional(),
});

// #2411 server-boundary variant — full depth, including the two linked
// substance children the mobile app parses permissively.
export const ProjectCoshhRecordStrict = ProjectCoshhRecordSchema.extend({
  substance: CoshhSubstanceStrict.optional(),
  customSubstance: CustomCoshhStrict.optional(),
}).strict();

export type ProjectCoshhRecord = z.infer<typeof ProjectCoshhRecordSchema>;

/**
 * POST /api/mobile/v1/projects/:projectId/coshh body. Links a catalogue
 * substance to the project (with optional notes).
 */
export const ProjectCoshhCreateRequest = z
  .object({
    // Link EITHER a catalogue substance OR an account custom / SDS substance —
    // exactly one (mirrors the web route). `substanceId` stays first + optional
    // so existing catalogue-only clients keep working unchanged.
    substanceId: z.string().min(1).optional(),
    accountCustomCoshhId: z.string().min(1).optional(),
    notes: z.string().nullable().optional(),
    // G5-2 (#992) site-specific assessment — optional so existing clients keep
    // working; lets mobile produce a genuinely site-specific COSHH assessment.
    quantityUse: z.string().nullable().optional(),
    location: z.string().nullable().optional(),
    personsExposed: z.string().nullable().optional(),
    siteControls: z.string().nullable().optional(),
    residualRisk: z.string().nullable().optional(),
  })
  .strict()
  .refine(
    (d) => Boolean(d.substanceId) !== Boolean(d.accountCustomCoshhId),
    {
      message:
        "provide exactly one of substanceId (catalogue) or accountCustomCoshhId (custom)",
      path: ["substanceId"],
    },
  );

export type ProjectCoshhCreateRequest = z.infer<
  typeof ProjectCoshhCreateRequest
>;

/**
 * PATCH /api/mobile/v1/projects/:projectId/coshh/:substanceId body — only
 * the per-project notes can be edited; the underlying substance is
 * read-only.
 */
export const ProjectCoshhUpdateRequest = z
  .object({
    notes: z.string().nullable().optional(),
    // G5-2 (#992) site-specific assessment — editable per project per substance.
    quantityUse: z.string().nullable().optional(),
    location: z.string().nullable().optional(),
    personsExposed: z.string().nullable().optional(),
    siteControls: z.string().nullable().optional(),
    residualRisk: z.string().nullable().optional(),
  })
  .strict();

export type ProjectCoshhUpdateRequest = z.infer<
  typeof ProjectCoshhUpdateRequest
>;

/**
 * GET /api/mobile/v1/coshh/search response item — the search endpoint that
 * powers the mobile "add substance" picker. Mirrors the web search shape.
 */
export const CoshhSearchResult = z.object({
  substance: CoshhSubstanceSchema,
  confidence: z.number().min(0).max(1),
  matchedAlias: z.string().nullable(),
});

// #2411 server-boundary variant.
export const CoshhSearchResultStrict = CoshhSearchResult.extend({
  substance: CoshhSubstanceStrict,
}).strict();

export type CoshhSearchResult = z.infer<typeof CoshhSearchResult>;
